import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from 'react-hot-toast';
import api from "../utils/axios.ts";

import getApiErrorMessage from "../utils/helper.js";

// Dashboard summary hook - fetches all dashboard data
export const useDashboardSummary = () => {
  return useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: async () => {
      const { data } = await api.get('/dashboard/summary');
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
};

// Hook to fetch contribution trends
export const useContributionTrends = (period = '6months') => {
  return useQuery({
    queryKey: ['contribution-trends', period],
    queryFn: async () => {
      const { data } = await api.get('/contributions/statistics', { 
        params: { trend_period: period } 
      });
      return data;
    },
  });
};

// Hook to fetch loan analytics
export const useLoanAnalytics = () => {
  return useQuery({
    queryKey: ['loan-analytics'],
    queryFn: async () => {
      const { data } = await api.get('/loan-repayments/analytics');
      return data;
    },
  });
};

// Hook to fetch member statistics
export const useMemberStats = () => {
  return useQuery({
    queryKey: ['member-stats'],
    queryFn: async () => {
      const { data } = await api.get('/members/statistics');
      return data;
    },
  });
};

// Hook to fetch upcoming meetings
export const useUpcomingMeetings = () => {
  return useQuery({
    queryKey: ['upcoming-meetings'],
    queryFn: async () => {
      const { data } = await api.get('/meetings', { 
        params: { upcoming: true, limit: 5 } 
      });
      return data;
    },
  });
};

export const useKitBalance = () => {
    return useQuery({
      queryKey: ['kit-balance'],
      queryFn: async () => {
        const { data } = await api.get('/kit/balance');
        return data;
      },
    });
  };

export const useKitTransactions = (filters: {
  page?: number;
  per_page?: number;
  search?: string;
  member_id?: number;
} = {}) => {
    return useQuery({
      queryKey: ['kit-transactions', filters],
      queryFn: async () => {
        const { data } = await api.get('/kit/transactions', { params: filters });
        return data;
      },
    });
  };

  
export const useWithdrawKit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (details) => {
      const { data } = await api.post(`/kit/withdrawal/`, details);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kit-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['kit-balance'] });
      toast.success('Withdrawal request sent successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

export const useMemberDashboardData = () => {
  return useQuery({
    queryKey: ['member-dashboard-data'],
    queryFn: async () => {
      const { data } = await api.get('/dashboard/member-data');
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook to export transactions
export const useExportTransactions = () => {
  return useMutation({
    mutationFn: async (filters = {}) => {
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.member_id) params.append('member_id', filters.member_id.toString());
      if (filters.type) params.append('type', filters.type);
      
      const response = await api.get(`/kit/transactions/export?${params.toString()}`, {
        responseType: 'blob',
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Use the filename from the backend response headers if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'transactions';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // If no filename from headers, determine from type parameter
      if (filename === 'transactions') {
        const type = filters.type || 'excel';
        const extensions = {
          'excel': 'xlsx',
          'csv': 'csv', 
          'pdf': 'pdf'
        };
        filename = `transactions.${extensions[type] || 'xlsx'}`;
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error) || 'Failed to export transactions');
    },
  });
};